import { useCallback, useEffect, useMemo } from 'react';
import { IFormContext, useFormContext } from '../../Form/Context';
import { useForm } from '../../Form/useForm';
import { useField } from '../../Field/useField';
import { FieldInstance } from '../FieldInstance';
import { FieldErrorMap } from '../../Form/FieldErrorMap';
import { Validator } from '../FieldRenderProps';

export type SubFormField<
    TFieldValue extends any = any,
    TSubFormShape extends Record<string, any> = Record<string, any>,
> = {
    fieldValue: TFieldValue;
    subFormShape: TSubFormShape;
};

export type SubFormFieldMapper<TSubFormField extends SubFormField = SubFormField> = (
    subFormValues: Partial<TSubFormField['subFormShape']>
) => TSubFormField['fieldValue'];

export type SubFormFieldParser<TSubFormField extends SubFormField = SubFormField> = (
    fieldValue: TSubFormField['fieldValue']
) => TSubFormField['subFormShape'];

export type UseSubFormFieldProps<TSubFormField extends SubFormField = SubFormField> = {
    /**
     * The name of the field on the parent form
     */
    name: string;
    /**
     * A function that takes the current value of the field and returns the values of the sub form
     */
    fieldValueFromFormValues: SubFormFieldMapper<TSubFormField>;
    /**
     * A function that takes the current values of the sub form and returns the value for the field
     */
    formValuesToFieldValue: SubFormFieldParser<TSubFormField>;
};

export type UseSubFormFieldResults<TSubFormField extends SubFormField = SubFormField> = {
    formContext: IFormContext<TSubFormField['subFormShape']>;
    field: FieldInstance<TSubFormField['fieldValue']>;
};

/**
 * A hook that will start a child form context and provide mappings between it's parent form to support grouping fields
 * @param {UseSubFormFieldProps} props
 *
 * @returns
 */
export const useSubFormField = <TSubFormField extends SubFormField = SubFormField>({
    name,
    fieldValueFromFormValues,
    formValuesToFieldValue,
}: UseSubFormFieldProps<TSubFormField>): UseSubFormFieldResults<TSubFormField> => {
    const parentContext = useFormContext();
    const childContext = useForm<TSubFormField['subFormShape']>({
        initialValues: parentContext.initialValues[name],
    });

    const validateChildContext: Validator<TSubFormField> = useCallback(async () => {
        const results = await Promise.all(
            childContext.formFields.map((formField) => {
                return formField.validateOnSubmit();
            })
        );
        if (results.some((result) => result !== true)) {
            return { pass: false, message: '' };
        } else {
            return { pass: true };
        }
    }, [childContext.formFields]);

    const baseField = useField<TSubFormField['fieldValue']>({
        name: name,
        initialValue: parentContext.values[name],
        onSubmitValidate: validateChildContext,
    });

    const setValue = useCallback(
        (newValue: any) => {
            const parsedValue = formValuesToFieldValue(newValue);
            Object.entries(parsedValue).map(([key, value]) => {
                childContext.setFieldValue(key, value);
            });
        },
        [formValuesToFieldValue, childContext.setFieldValue]
    );

    const field = useMemo(() => {
        return { ...baseField, setValue };
    }, [baseField, setValue]);

    useEffect(() => {
        parentContext.setField(field);
    }, [parentContext.setField, field]);

    const mappedValue = useMemo(() => {
        return fieldValueFromFormValues(childContext.values);
    }, [fieldValueFromFormValues, childContext.values]);

    useEffect(() => {
        baseField.setValue(mappedValue);
    }, [baseField.setValue, mappedValue]);

    useEffect(() => {
        const subErrors = Object.entries(parentContext.errorMap).filter(([fieldId]) => {
            return fieldId.startsWith(`${name}.`);
        });
        const subErrorMap = subErrors.reduce(
            (subErrorMap, [fieldId, errors]): FieldErrorMap<TSubFormField> => {
                const subKey = fieldId.replace(`${name}.`, '');
                return { ...subErrorMap, [subKey]: errors };
            },
            {}
        );
        childContext.setErrors(subErrorMap);
    }, [name, parentContext.errorMap, childContext.setErrors]);

    useEffect(() => {
        const parentErrors = Object.entries(childContext.errorMap).reduce(
            (parentErrorMap, [fieldId, errors]): FieldErrorMap<any> => {
                const parentKey = `${name}.${fieldId}`;
                return { ...parentErrorMap, [parentKey]: errors };
            },
            {}
        );
        parentContext.setErrors(parentErrors);
    }, [name, childContext.errorMap, parentContext.setErrors]);

    const result = useMemo(() => {
        return {
            formContext: childContext,
            field,
        };
    }, [childContext, field]);

    return result;
};
